import { useEffect, useRef, useState } from "react";
import type { ClinicThreadSummary } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";
import { useChatPoll } from "@ferrivia/chat-ui";
import { subscribeChatSummariesStale } from "./chatSummaryBus";

export type ChatSummariesState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; summaries: ClinicThreadSummary[] };

export function useChatSummaries(): ChatSummariesState {
  const api = useApi();
  const [state, setState] = useState<ChatSummariesState>({ status: "loading" });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const { poll } = useChatPoll(
    async () => {
      try {
        const summaries = await api.clinicChat.summaries();
        if (!cancelledRef.current) {
          setState({ status: "success", summaries });
        }
      } catch (err) {
        if (!cancelledRef.current) {
          setState((prev) =>
            prev.status === "success" ? prev : { status: "error", error: err instanceof Error ? err.message : "Failed to load chat summaries" },
          );
        }
        throw err;
      }
    },
    { focusedIntervalMs: 15_000, backgroundIntervalMs: 45_000 },
  );

  useEffect(() => subscribeChatSummariesStale(poll), [poll]);

  return state;
}
